import React, { useMemo, useState } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { refs } from "../../convex/apiRefs";
import "./jev.css";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// JevFav2 (#652) — the Jev window's "FAV 2.0" tab: every FAV 2.0 answer the blackout-dates
// Lambda gave (booked FAV calls AND FileMaker's advice-only reserve:false calls), newest
// first, with the plain-English "why" FAV 2.0 wrote from its own decision. Reads the PROD
// box (like the rest of the Jev window) through scheduler/fav2Log:listRecent ("jev"-gated).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const fmt = (ms) => (ms ? new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const md = (iso) => {
  const m = String(iso || "").match(/^\d{4}-(\d{2})-(\d{2})$/);
  return m ? `${+m[1]}/${+m[2]}` : "—";
};

/** The result cell: "1d · 3928.01 · 11/2 · partial 47%" or "skipped · <reason>". */
export function fav2Result(r) {
  if (r.status !== "ok") return `skipped · ${String(r.reason || "?").replace(/_/g, " ")}`;
  const when = r.endDateISO && r.endDateISO !== r.startDateISO ? `${md(r.startDateISO)}–${md(r.endDateISO)}` : md(r.startDateISO);
  const kind = r.dayKind === "partial" ? ` · partial ${Math.round((r.fillAfterPct || 0) * 100)}%` : "";
  return `${r.workingDays}d · ${r.installer} · ${when}${kind}`;
}

/** Filter the log rows by office / result / kind of call. Pure (tested). */
export function filterFav2Rows(rows, { office = "", result = "", call = "" } = {}) {
  return (rows || []).filter(
    (r) =>
      (!office || r.location === office) &&
      (!result || (result === "ok" ? r.status === "ok" : r.status !== "ok")) &&
      (!call || (call === "advice" ? r.adviceOnly : !r.adviceOnly))
  );
}

export default function JevFav2() {
  const rows = useConvexQuery(refs.fav2LogList, { limit: 300 });
  const [office, setOffice] = useState("");
  const [result, setResult] = useState("");
  const [call, setCall] = useState("");
  const [selected, setSelected] = useState(null);
  const offices = useMemo(() => [...new Set((rows || []).map((r) => r.location))].sort(), [rows]);
  const shown = useMemo(() => filterFav2Rows(rows, { office, result, call }), [rows, office, result, call]);

  if (rows === undefined) return <div className="jev-root"><div className="jev-empty">Loading FAV 2.0 answers…</div></div>;
  const sel = shown.find((r) => r.id === selected) || null;
  const okCount = shown.filter((r) => r.status === "ok").length;

  return (
    <div className="jev-root" style={{ display: "grid", gap: 12 }} data-testid="jev-fav2">
      <div className="jev-controls">
        <label>
          Office{" "}
          <select value={office} onChange={(e) => setOffice(e.target.value)}>
            <option value="">All</option>
            {offices.map((o) => <option key={o}>{o}</option>)}
          </select>
        </label>
        <label>
          Result{" "}
          <select value={result} onChange={(e) => setResult(e.target.value)}>
            <option value="">All</option>
            <option value="ok">Found a crew</option>
            <option value="skipped">Skipped</option>
          </select>
        </label>
        <label>
          Call{" "}
          <select value={call} onChange={(e) => setCall(e.target.value)}>
            <option value="">All</option>
            <option value="booked">FAV booked</option>
            <option value="advice">Advice only</option>
          </select>
        </label>
        <span className="jev-note">
          Newest {rows.length} answers · showing {shown.length} · {okCount} found a crew
        </span>
      </div>
      <div className="jev-tablewrap">
        <table>
          <thead>
            <tr>
              {["Time", "Office", "Keller ID", "Call", "FAV 2.0", "Today's FAV"].map((h) => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className={r.id === selected ? "sel" : ""} onClick={() => setSelected(r.id)} data-testid="jev-fav2-row">
                <td>{fmt(r.at)}</td>
                <td>{r.location}</td>
                <td>{r.kellerId}</td>
                <td>{r.adviceOnly ? "advice only" : "FAV booked"}</td>
                <td>{fav2Result(r)}</td>
                <td>{r.v1DateISO ? `${md(r.v1DateISO)}${r.v1Reserved ? "" : " (not booked)"}` : "—"}</td>
              </tr>
            ))}
            {!shown.length && (
              <tr><td colSpan={6} className="jev-empty">No FAV 2.0 answers{office || result || call ? " match" : " yet"}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="jev-card" data-testid="jev-fav2-detail">
          <h3>{sel.kellerId} · {sel.location} · {fmt(sel.at)}</h3>
          <div style={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>{sel.why || "No explanation recorded."}</div>
          <div className="jev-note" style={{ marginTop: 8 }}>
            Rule {sel.fav2Version || "—"} · Lambda {sel.lambdaVersion}{sel.daysConfidence != null ? ` · ${Math.round(sel.daysConfidence * 100)}% chance the days are enough` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
